import React, {Component, ReactElement, ReactNode} from 'react';
import I18n from 'app/i18n';
import {getLinkPath, isDesktop} from 'app/utils';
import {navigateGoTo} from 'app/utils/navigate/navigateGoTo';
import {NavLink} from 'react-router-dom';
import DepositHistory from './DepositHistory';
import {getDepositComponent} from './DepositComponents';
import Systems from 'app/components/deposit/Systems';
import {inject, observer} from 'mobx-react';
import {reaction, IReactionDisposer} from 'mobx';
import P2pDepositsStoreProps from 'app/store/P2pDepositsStoreProps';
import {DepositsStoreProps} from 'app/store/DepositsStore';
import PaymentSystemInterface from 'app/interfaces/PaymentSystemInterface';
import SmartPageLoader from 'app/components/reusable/smart_loaders/SmartPageLoader';
import Get from 'app/utils/Get';
import withRouter from 'app/utils/navigate/customWithRouter';
import DisabledCountryMessage from 'app/components/common/DisabledCountryMessage';
import SelectPicker from 'app/components/reusable/SelectPicker';
import CollectionInterface from 'app/interfaces/CollectionInterface';
import {configStore, userStore} from 'app/store/global';
import SpriteIcon from 'app/utils/sprites/SpriteIcon';
import FilteredDeposits from 'app/components/deposit/p2p/FilteredDeposits';
import HelpSupport from 'app/components/deposit/form/HelpSupport';

export interface DepositPageProps extends P2pDepositsStoreProps, DepositsStoreProps {
    params?: {
        system_slug: string
    }
}

class DepositPage extends Component<DepositPageProps> {
    private disposeReaction?: IReactionDisposer;

    state = {
        countryCode: userStore.user.country_code,
        countryCodeChanged: false
    };

    fetchDepositsData = (): void => {
        new Get({params: {country_code: this.state.countryCode}, url: getLinkPath('/deposits')})
            .execute()
            .then(response => response.json())
            .then(data => {
                if (data) {
                    this.props.p2pDepositsStore.update(null, null, data.current_p2p_deposits);
                    this.props.depositsStore.update(data);

                    if (this.props.params.system_slug) {
                        const {systems} = this.props.depositsStore;
                        const {system_slug} = this.props.params;
                        this.selectSystem(systems.find(system => system.slug === system_slug), true);
                    } else if (isDesktop()) {
                        this.selectFirstRegisteredSystem(true);
                    }
                }
            });
    };

    componentDidMount(): void {
        const {loading} = this.props.depositsStore;

        if (loading) {
            this.fetchDepositsData();
        }

        this.disposeReaction = reaction(
            () => userStore.user.country_code,
            code => {
                if (!this.state.countryCodeChanged) {
                    this.setState({countryCode: code});
                }
            },
            {fireImmediately: true}
        );
    }

    componentDidUpdate(oldProps: DepositPageProps, prevState: {countryCode: string}): void {
        const {system_slug} = this.props.params;

        if (oldProps.params.system_slug !== system_slug) {
            const {systems} = this.props.depositsStore;
            const system = systems.find(s => s.slug === system_slug);
            this.updatePaymentSystemState(system);
        }

        if (prevState.countryCode !== this.state.countryCode) {
            this.fetchDepositsData();
        }
    }

    componentWillUnmount = (): void => {
        this.props.depositsStore.unLoad();
        this.props.depositsStore.selectPaymentSystem(null);
        this.props.p2pDepositsStore.selectPaymentSystem(null);
        this.disposeReaction?.();
    };

    get hasSystems(): boolean {
        const {systems} = this.props.depositsStore;
        return systems?.length > 0;
    }

    updatePaymentSystemState = (system: PaymentSystemInterface): void => {
        const {current_p2p_deposit} = this.props.p2pDepositsStore;

        if (!current_p2p_deposit || !current_p2p_deposit?.working) {
            this.props.depositsStore.selectPaymentSystem(system);
            this.props.p2pDepositsStore.selectPaymentSystem(system);
        }
    };

    selectSystem = (system: PaymentSystemInterface, replace = false): void => {
        const {system_slug} = this.props.params || {};

        if (system) {
            if (system.slug !== system_slug) {
                navigateGoTo(getLinkPath(`/deposits${system ? `/${system.slug}` : ''}`), {}, replace);
            }
            this.updatePaymentSystemState(system);
        }
    };

    selectFirstRegisteredSystem = (replace = false): void => {
        if (this.hasSystems) {
            const {systems} = this.props.depositsStore;
            const registeredSystems = this.getRegisteredSystems();
            const selectSystem = registeredSystems.length > 0 ? registeredSystems[0] : systems[0];
            this.selectSystem(selectSystem, replace);
        }
    };

    getSystems(recommended = true): PaymentSystemInterface[] {
        const {systems} = this.props.depositsStore;
        return systems.filter(system => system.recommended === recommended && !system.registered);
    }

    getRegisteredSystems(): PaymentSystemInterface[] {
        const {systems} = this.props.depositsStore;
        return systems.filter(system => system.registered);
    }

    getSystemComponent = (): ReactElement => {
        const {currentPaymentSystem} = this.props.depositsStore;

        if (!currentPaymentSystem) {
            this.selectFirstRegisteredSystem(true);
            return null;
        }

        const {deposit_ui_component} = currentPaymentSystem;
        const UiComponent: React.ComponentType<DepositPageProps> = getDepositComponent(deposit_ui_component);

        if (!UiComponent) {
            throw new Error(`invalid ui component ${deposit_ui_component}`);
        }

        return <UiComponent {...this.props}/>;
    };

    availableFlags = (): CollectionInterface[] => configStore.REG_FLAGS.map((flag): CollectionInterface => ({
        image: flag.image,
        text: flag.name,
        value: flag.code
    }));

    handleOnChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
        const selectedValue = e.target.value;
        this.setState({countryCode: selectedValue, countryCodeChanged: true});
        this.props.depositsStore.changeSelectedCountry(selectedValue);
    };

    userDepositCountry = (): ReactElement => <div className="user-region-settings">
        <p className="user-region-settings__title">{I18n.t('user.deposit_country')}</p>
        <div className="classical-select select-with-images">
            <SelectPicker
                onChange={this.handleOnChange}
                name="country_code"
                value={this.state.countryCode}
                collection={this.availableFlags()}
                placeholder={I18n.t('select_a_country')}
                id="field-country"
                className="select-country"
            />
        </div>
    </div>;

    render(): ReactNode {
        const system = this.props.depositsStore.currentPaymentSystem;
        const {loading, success, message, rates} = this.props.depositsStore;
        const {filteredDeposits, current_p2p_deposit} = this.props.p2pDepositsStore;


        if (loading) {
            return <div className="deposit">
                <div className="deposit-title">
                    <h2 className="title-h1">{I18n.t('user.deposit')}</h2>
                    {this.userDepositCountry()}
                </div>
                <SmartPageLoader/>
            </div>;
        }

        if (!success) {
            return <div className="deposit">
                <div className="deposit-title">
                    <h2 className="title-h1">{I18n.t('user.deposit')}</h2>
                    {this.userDepositCountry()}
                </div>
                {message === 'disabled_country'
                    ? <DisabledCountryMessage className="deposit__info"/>
                    : <div className="deposit__info" dangerouslySetInnerHTML={{__html: message}}/>}
            </div>;
        }

        return <div className="deposit">
            {system && !isDesktop()
                ? <>
                    <div className="title title__selectedSystem">
                        <NavLink to={getLinkPath('/deposits')}>
                            <>
                                <SpriteIcon name="#svg-back" size={{height: 8, width: 5}}/>
                                {I18n.t('button_back_label')}
                            </>
                        </NavLink>
                        <h1>{`${I18n.t('user.deposit')} ${system.name}`}</h1>
                    </div>
                    {this.getSystemComponent()}
                    {!current_p2p_deposit && (system?.p2p
                        ? <FilteredDeposits filteredDeposits={filteredDeposits} system={system} rates={rates} />
                        : <div className="payment-systems__support-online"><HelpSupport/></div>)}
                </>
                : <>
                    <div className="deposit-title">
                        {isDesktop()
                            ? <h2 className="title-h1">{I18n.t('user.deposit')}</h2>
                            : <h1 className="title-h1">{I18n.t('user.deposit')}</h1>}
                        {this.userDepositCountry()}
                    </div>
                    <div className="payment-systems payment-systems__deposit">
                        <div className="payment-systems__container">
                            <div className="payment-systems__deposit-lists">
                                <Systems
                                    title={I18n.t('registered_payment_method')}
                                    systems={this.getRegisteredSystems()}
                                    selectSystem={this.selectSystem}
                                />
                                <Systems
                                    title={I18n.t('recommended_for_your_country')}
                                    systems={this.getSystems()}
                                    selectSystem={this.selectSystem}
                                />
                                <Systems
                                    title={I18n.t('other_methods')}
                                    systems={this.getSystems(false)}
                                    selectSystem={this.selectSystem}
                                />
                            </div>
                            {isDesktop() && <div className="payment-systems__deposit-details">
                                <div className="payment-systems__selectedSystem">
                                    <div className="title title__selectedSystem">
                                        <h1>{`${I18n.t('user.deposit')} ${system?.name}`}</h1>
                                    </div>
                                    {this.hasSystems && this.getSystemComponent()}
                                </div>
                                {system?.p2p && !current_p2p_deposit
                                    ? <FilteredDeposits filteredDeposits={filteredDeposits} system={system} rates={rates} />
                                    : <div className="payment-systems__support-online"><HelpSupport/></div>}
                            </div>}
                        </div>
                    </div>
                </>}
            <div className="history-transferred">
                <DepositHistory/>
            </div>
        </div>;
    }
}

export default withRouter(inject('depositsStore', 'p2pDepositsStore')(observer(DepositPage)));
